import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: 'left' | 'right';
}

// Simple click-toggle popover. Click outside or Escape closes. No focus
// trap, no roving tabindex — for the low-stakes menus we have (user menu,
// row actions), browser default behavior is fine.
export function DropdownMenu(props: DropdownMenuProps) {
  const { trigger, children, align = 'right' } = props;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <div onClick={() => setOpen((v) => !v)}>{trigger}</div>
      {open && (
        <div
          className={cn(
            'absolute z-40 mt-1 min-w-[180px] rounded-md border border-brand-200 bg-white py-1 shadow-lg',
            align === 'right' ? 'right-0' : 'left-0',
          )}
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}

interface DropdownItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  destructive?: boolean;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export function DropdownItem(props: DropdownItemProps) {
  const { destructive, icon, className, children, disabled, ...rest } = props;
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
        destructive ? 'text-red-700 hover:bg-red-50' : 'text-brand-700 hover:bg-brand-50',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
      {...rest}
    >
      {icon && <span className="text-brand-400">{icon}</span>}
      {children}
    </button>
  );
}

export function DropdownSeparator() {
  return <div className="my-1 h-px bg-brand-100" />;
}
